import {useQueryLoader, type PreloadedQuery, usePreloadedQuery, useFragment, graphql} from 'react-relay'
import {RelationsSectionFallback, RelationsSectionInternal} from './sections/RelationsSection'
import {DevelopmentSection, DevelopmentSectionFallback} from './sections/development-section/DevelopmentSection'
import type {IssueViewerSecondaryViewQuery} from './__generated__/IssueViewerSecondaryViewQuery.graphql'
import {IssueViewerSecondaryGraphqlQuery} from './IssueViewer'
import {useEffect} from 'react'
import {SubscriptionSection, SubscriptionSectionFallback} from './sections/SubscriptionSection'
import type {IssueSidebarLazySections$key} from './__generated__/IssueSidebarLazySections.graphql'

type IssueSidebarLazySectionsBaseProps = {
  onLinkClick?: (event: MouseEvent) => void
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  tasklist_block?: boolean
}

type IssueSidebarLazySectionsProps = IssueSidebarLazySectionsBaseProps & {
  issueNumber: number
  owner: string
  repo: string
}

type IssueSidebarLazySectionsInternalProps = IssueSidebarLazySectionsBaseProps & {
  issueViewerSecondaryViewRef: PreloadedQuery<IssueViewerSecondaryViewQuery>
}

type IssueSidebarLazySectionsFetchedProps = IssueSidebarLazySectionsBaseProps & {
  issueSidebarSecondaryKey: IssueSidebarLazySections$key
}

type FallbackProps = {
  tasklist_block?: boolean
}

export function IssueSidebarLazySections({owner, repo, issueNumber, ...rest}: IssueSidebarLazySectionsProps) {
  const [issueViewerSecondaryViewRef, loadIssueViewerSecondaryView] = useQueryLoader<IssueViewerSecondaryViewQuery>(
    IssueViewerSecondaryGraphqlQuery,
  )

  useEffect(() => {
    loadIssueViewerSecondaryView({owner, repo, number: issueNumber, markAsRead: true})
  }, [loadIssueViewerSecondaryView, owner, repo, issueNumber])

  if (!issueViewerSecondaryViewRef) {
    return <IssueSidebarLazySectionsFallback tasklist_block={rest.tasklist_block} />
  }

  return <IssueSidebarLazySectionsInternal issueViewerSecondaryViewRef={issueViewerSecondaryViewRef} {...rest} />
}

function IssueSidebarLazySectionsInternal({
  issueViewerSecondaryViewRef,
  ...rest
}: IssueSidebarLazySectionsInternalProps) {
  const preloadedData = usePreloadedQuery<IssueViewerSecondaryViewQuery>(
    IssueViewerSecondaryGraphqlQuery,
    issueViewerSecondaryViewRef,
  )

  if (!preloadedData.repository?.issue) {
    return <IssueSidebarLazySectionsFallback tasklist_block={rest.tasklist_block} />
  }

  return <IssueSidebarLazySectionsFetched issueSidebarSecondaryKey={preloadedData.repository?.issue} {...rest} />
}

function IssueSidebarLazySectionsFetched({
  issueSidebarSecondaryKey,
  onIssueUpdate,
  onLinkClick,
  singleKeyShortcutsEnabled,
  tasklist_block,
}: IssueSidebarLazySectionsFetchedProps) {
  const data = useFragment(
    graphql`
      fragment IssueSidebarLazySections on Issue {
        ...DevelopmentSectionFragment
        ...RelationsSectionFragment
        ...SubscriptionSectionFragment
      }
    `,
    issueSidebarSecondaryKey,
  )

  return (
    <>
      {tasklist_block && <RelationsSectionInternal issue={data} onLinkClick={onLinkClick} />}
      <DevelopmentSection
        singleKeyShortcutsEnabled={singleKeyShortcutsEnabled}
        issue={data}
        onIssueUpdate={onIssueUpdate}
      />
      <SubscriptionSection issue={data} />
    </>
  )
}

export function IssueSidebarLazySectionsFallback({tasklist_block}: FallbackProps) {
  return (
    <>
      {tasklist_block && <RelationsSectionFallback />}
      <DevelopmentSectionFallback />
      <SubscriptionSectionFallback />
    </>
  )
}

try{ IssueSidebarLazySections.displayName ||= 'IssueSidebarLazySections' } catch {}
try{ IssueSidebarLazySectionsInternal.displayName ||= 'IssueSidebarLazySectionsInternal' } catch {}
try{ IssueSidebarLazySectionsFetched.displayName ||= 'IssueSidebarLazySectionsFetched' } catch {}
try{ IssueSidebarLazySectionsFallback.displayName ||= 'IssueSidebarLazySectionsFallback' } catch {}